import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui";
import { X } from "lucide-react";
import { FC } from "react";
import useInstallmentSchedule from "../useInstallmentSchedule";
import { FormProvider, useFormContext } from "react-hook-form";
import { FormDatePicker, FormInput, FormSelect } from "@/components";
import {
  EXPECTED_DUE_DATE,
  INSTALLMENT_AMOUNT,
  INSTALLMENT_NUMBER,
  INSTALLMENT_YEAR,
  MOU_AMOUNT,
  INTEREST_PERCENTAGE,
  INSTALLMENT
} from "../consts";
import { yearOptions } from "@/lib/utils";

const AddNewInstallmentDrawer: FC<any> = ({ open, onCancel }) => {
  const { InstallmentForm, onSubmitNewInstallment, requiredFields } =
    useInstallmentSchedule({
      onNextClick: () => {},
    });

  const { control, setValue, watch, formState: { errors } } = InstallmentForm;

  // 🧮 Watch values for auto-calculation
  const mouAmount = watch(MOU_AMOUNT);
  const interestPercent = watch(INTEREST_PERCENTAGE);

  // 💡 Auto-calculate Interest Amount whenever MOU Amount or % changes
  useEffect(() => {
    const amount = parseFloat(mouAmount);
    const percent = parseFloat(interestPercent);

    if (!isNaN(amount) && !isNaN(percent)) {
      const interestAmount = (amount * percent) / 100;
      setValue(INSTALLMENT_AMOUNT, interestAmount.toFixed(2)); // two decimal places
    } else {
      setValue(INSTALLMENT_AMOUNT, "");
    }
  }, [mouAmount, interestPercent, setValue]);

  const onSubmit = (data: any) => {
    onSubmitNewInstallment(data);
    onCancel();
  };

  return (
    <Drawer open={open} onOpenChange={onCancel} direction="right">
      <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">Add Installment Details</DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...InstallmentForm}>
          <form onSubmit={InstallmentForm.handleSubmit(onSubmit)} className="relative">
            <div className="relative bg-white rounded-lg pt-[30px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[130px] px-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[25px]">
                <FormSelect
                  name={INSTALLMENT_YEAR}
                  control={control}
                  label="Year"
                  placeholder="Please select"
                  error={errors[INSTALLMENT_YEAR]?.message}
                  isRequired={requiredFields.includes(
                    `${INSTALLMENT}[].${INSTALLMENT_YEAR}`
                  )}
                  options={yearOptions()}
                />

                <FormInput
                  name={INSTALLMENT_NUMBER}
                  control={control}
                  label="Installment #"
                  placeholder="Please enter"
                  error={errors[INSTALLMENT_NUMBER]?.message}
                  isRequired={requiredFields.includes(
                    `${INSTALLMENT}[].${INSTALLMENT_NUMBER}`
                  )}
                />

                <FormInput
                  name={MOU_AMOUNT}
                  control={control}
                  label="MOU Amount"
                  placeholder="Please enter"
                  prefix="₹"
                  error={errors[MOU_AMOUNT]?.message}
                  isRequired={requiredFields.includes(
                    `${INSTALLMENT}[].${MOU_AMOUNT}`
                  )}
                />

                <FormInput
                  name={INTEREST_PERCENTAGE}
                  control={control}
                  label="InstallmentInterest %"
                  placeholder="Please enter"
                  error={errors[INTEREST_PERCENTAGE]?.message}
                  isRequired={requiredFields.includes(
                    `${INSTALLMENT}[].${INTEREST_PERCENTAGE}`
                  )}
                />

                

                <FormDatePicker
                  name={EXPECTED_DUE_DATE}
                  control={control}
                  label="Expected Due Date"
                  placeholder="Please select"
                  error={errors[EXPECTED_DUE_DATE]?.message}
                  isRequired={requiredFields.includes(
                    `${INSTALLMENT}[].${EXPECTED_DUE_DATE}`
                  )}
                />

                <FormInput
                  name={INSTALLMENT_AMOUNT}
                  control={control}
                  label="Installment Amount"
                  prefix="₹"
                  placeholder="Please enter"
                  error={errors[INSTALLMENT_AMOUNT]?.message}
                  isRequired={requiredFields.includes(
                    `${INSTALLMENT}[].${INSTALLMENT_AMOUNT}`
                  )}
                  disabled
                />
              </div>
            </div>

            <div className="flex flex-row gap-3 absolute bottom-3 w-full bg-white justify-end pb-[25px] z-[3]">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit">Submit</Button>
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default AddNewInstallmentDrawer;
